import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, LogOut, X, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { MarkdownEditor, uploadImage } from "../components/MarkdownEditor";

const CATS = ["Affiliate Marketing", "Performance Marketing", "Publisher Strategy",
  "Customer Acquisition", "Digital Advertising", "Industry Insights"];

const empty = {
  title_en: "", title_es: "", slug: "", category: "Industry Insights",
  cover_image: "", excerpt_en: "", excerpt_es: "", body_en: "", body_es: "",
  author: "WANM Editorial", publish_date: "",
  seo_title_en: "", seo_title_es: "", seo_desc_en: "", seo_desc_es: "",
  published: true,
};

const Input = ({ label, value, onChange, required, testId, placeholder }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">{label}</label>
    <input value={value || ""} onChange={onChange} required={required} data-testid={testId} placeholder={placeholder}
      className="w-full border-b-2 border-wanm-dark/20 py-2 focus:outline-none focus:border-wanm-pink" />
  </div>
);

const AdminDashboard = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const logout = useCallback(() => { localStorage.removeItem("wanm_token"); nav("/admin"); }, [nav]);

  const load = useCallback(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => logout());
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("wanm_token")) { nav("/admin"); return; }
    load();
  }, [load, nav]);

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = async (a) => {
    const { data } = await api.get(`/articles/${a.slug}`);
    setEditing(a.id); setForm({ ...empty, ...data });
  };
  const F = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing && editing !== "new") await api.put(`/admin/articles/${editing}`, form);
      else await api.post("/admin/articles", form);
      setEditing(null); setForm(empty); load();
    } catch { alert("Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (window.confirm("Delete this article?")) { await api.delete(`/admin/articles/${id}`); load(); } };

  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCoverUploading(true);
    try { setField("cover_image", await uploadImage(file)); }
    catch { alert("Cover upload failed"); }
    finally { setCoverUploading(false); e.target.value = ""; }
  };

  return (
    <div className="min-h-screen bg-wanm-offwhite">
      <header className="bg-white border-b border-wanm-offwhite sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-6 h-20 flex items-center justify-between">
          <Logo variant="primary" height={34} to="/" />
          <div className="flex items-center gap-4">
            <button onClick={() => setTab("leads")} className={`font-bold ${tab === "leads" ? "text-wanm-red" : "text-wanm-dark/60"}`} data-testid="tab-leads">Leads</button>
            <button onClick={() => setTab("articles")} className={`font-bold ${tab === "articles" ? "text-wanm-red" : "text-wanm-dark/60"}`} data-testid="tab-articles">Articles</button>
            <button onClick={logout} className="flex items-center gap-1.5 text-wanm-dark/60 hover:text-wanm-red font-bold" data-testid="logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        {tab === "leads" && (
          <div>
            <h1 className="text-3xl font-black text-wanm-dark mb-6">Submissions ({leads.length})</h1>
            {leads.length === 0 ? <p className="text-wanm-dark/50">No submissions yet.</p> : (
              <div className="space-y-4">
                {leads.map((l) => (
                  <div key={l.id} className="bg-white rounded-2xl p-6" data-testid={`lead-${l.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-black uppercase tracking-widest wanm-gradient-text">{l.type}</span>
                      <span className="text-xs text-wanm-dark/40">{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                      {Object.entries(l).filter(([k]) => !["id", "type", "created_at"].includes(k)).map(([k, v]) => (
                        v ? <div key={k}><span className="font-bold text-wanm-dark/70">{k}: </span><span className="text-wanm-dark/60">{String(v)}</span></div> : null
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "articles" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-black text-wanm-dark">Articles ({articles.length})</h1>
              <button onClick={openNew} data-testid="new-article"
                className="inline-flex items-center gap-2 wanm-gradient-bg text-white font-bold rounded-full px-5 py-2.5"><Plus size={18} /> New</button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="bg-white rounded-xl p-5 flex items-center justify-between" data-testid={`article-row-${a.id}`}>
                  <div>
                    <p className="font-black text-wanm-dark">{a.title_en}</p>
                    <p className="text-xs text-wanm-dark/50">{a.category} · {a.published ? "Published" : "Draft"} · /{a.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(a)} className="text-wanm-dark/60 hover:text-wanm-red font-bold text-sm px-3" data-testid={`edit-${a.id}`}>Edit</button>
                    <button onClick={() => del(a.id)} className="text-wanm-dark/60 hover:text-wanm-red" data-testid={`del-${a.id}`}><Trash2 size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
          <form onSubmit={save} className="bg-white rounded-3xl p-8 w-full max-w-3xl my-8 space-y-5" data-testid="article-form">
            <div className="flex items-center justify-between sticky top-0 bg-white pb-2">
              <h2 className="text-2xl font-black text-wanm-dark">{editing === "new" ? "New Article" : "Edit Article"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="article-close"><X /></button>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Input label="Title (EN)" value={form.title_en} onChange={F("title_en")} required testId="af-title_en" />
              <Input label="Title (ES)" value={form.title_es} onChange={F("title_es")} required testId="af-title_es" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Input label="Slug (auto if empty)" value={form.slug} onChange={F("slug")} testId="af-slug" placeholder="my-article" />
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">Category</label>
                <select value={form.category} onChange={F("category")} data-testid="af-category" className="w-full border-b-2 border-wanm-dark/20 py-2 focus:outline-none">
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Input label="Author" value={form.author} onChange={F("author")} testId="af-author" />
              <Input label="Publish date (YYYY-MM-DD)" value={form.publish_date} onChange={F("publish_date")} testId="af-publish_date" placeholder="auto = today" />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-2">Cover image</label>
              <div className="flex items-center gap-4">
                {form.cover_image && <img src={form.cover_image} alt="" className="w-28 h-20 object-cover rounded-lg" />}
                <label className="inline-flex items-center gap-2 border-2 border-wanm-dark/15 rounded-full px-4 py-2 cursor-pointer hover:border-wanm-pink text-sm font-bold" data-testid="af-cover-upload">
                  {coverUploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Upload
                  <input type="file" accept="image/*" className="hidden" onChange={onCover} />
                </label>
                <input value={form.cover_image || ""} onChange={F("cover_image")} placeholder="or paste image URL"
                  className="flex-1 border-b-2 border-wanm-dark/20 py-2 focus:outline-none focus:border-wanm-pink text-sm" data-testid="af-cover_image" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">Excerpt (EN)</label>
                <textarea value={form.excerpt_en} onChange={F("excerpt_en")} rows={2} data-testid="af-excerpt_en" className="w-full border-2 border-wanm-dark/15 rounded-lg p-3 focus:outline-none focus:border-wanm-pink resize-none" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">Excerpt (ES)</label>
                <textarea value={form.excerpt_es} onChange={F("excerpt_es")} rows={2} data-testid="af-excerpt_es" className="w-full border-2 border-wanm-dark/15 rounded-lg p-3 focus:outline-none focus:border-wanm-pink resize-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-2">Body (EN)</label>
              <MarkdownEditor value={form.body_en} onChange={(v) => setField("body_en", v)} testId="af-body_en" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-2">Body (ES)</label>
              <MarkdownEditor value={form.body_es} onChange={(v) => setField("body_es", v)} testId="af-body_es" />
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Input label="SEO title (EN)" value={form.seo_title_en} onChange={F("seo_title_en")} testId="af-seo_title_en" />
              <Input label="SEO title (ES)" value={form.seo_title_es} onChange={F("seo_title_es")} testId="af-seo_title_es" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">SEO description (EN)</label>
                <textarea value={form.seo_desc_en} onChange={F("seo_desc_en")} rows={2} data-testid="af-seo_desc_en" className="w-full border-2 border-wanm-dark/15 rounded-lg p-3 focus:outline-none focus:border-wanm-pink resize-none" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-1">SEO description (ES)</label>
                <textarea value={form.seo_desc_es} onChange={F("seo_desc_es")} rows={2} data-testid="af-seo_desc_es" className="w-full border-2 border-wanm-dark/15 rounded-lg p-3 focus:outline-none focus:border-wanm-pink resize-none" />
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer" data-testid="af-published-label">
              <input type="checkbox" checked={form.published} onChange={(e) => setField("published", e.target.checked)} data-testid="af-published" className="w-5 h-5 accent-pink-500" />
              <span className="font-bold text-wanm-dark">Published {form.published ? "" : "(saved as Draft)"}</span>
            </label>

            <button type="submit" disabled={saving} data-testid="save-article"
              className="w-full wanm-gradient-bg text-white font-bold rounded-full py-3.5 disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
